// Dok 30c / Dok 10b – samordnade dokument från flera projektörer.
import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, h1, h2, table, factTable, tableOfContents } from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";
import {
  WORK_AREA_STATUS_LABELS,
  isIncluded,
  resolvedRiskLevel,
  riskLevelLabel,
  type ConsolidationSource,
  type Resolutions,
  type RiskGroup,
  type WorkAreaGroup,
} from "@/lib/basProcess/consolidation";

interface BaseArgs {
  projectName?: string;
  title: string;
  version?: string;
  status?: string;
  sources: ConsolidationSource[];
  resolutions: Resolutions;
  notes?: string;
}

const sourceTable = (sources: ConsolidationSource[]) =>
  table(
    ["Källa", "Typ", "Disciplin/företag"],
    sources.map((s) => [
      s.label,
      s.kind === "designer" ? "Projektörsinlämning (Dok 16)" : s.kind === "survey" ? "Arbetsområdesutredning (Dok 10)" : "Risksammanställning",
      [s.discipline, s.company].filter(Boolean).join(" · "),
    ]),
    { columnWidths: [3600, 3200, 2800] }
  );

export const generateConsolidatedRiskWord = async (
  args: BaseArgs & { groups: RiskGroup[]; phaseLabel: string }
) => {
  const documentName = args.title || "Samordnad risksammanställning";
  const stamp = await createStampForCurrentUser({
    docNumber: "30c",
    documentName,
    projectName: args.projectName,
  });

  const sourceLabel = new Map(args.sources.map((s) => [s.id, s.label]));
  const included = args.groups.filter((g) => isIncluded(args.resolutions, g.key));

  const children: (Paragraph | Table)[] = [
    ...tableOfContents(),
    h1("Om dokumentet"),
    body(
      "Dokumentet samordnar risker från flera projektörer och risksammanställningar till en gemensam bild. " +
        "Där källorna gjort olika bedömningar redovisas skillnaden och Bas-P:s beslut."
    ),
    factTable([
      ["Skede", args.phaseLabel],
      ["Antal källor", String(args.sources.length)],
      ["Antal risker", String(included.length)],
      ["Avvikande bedömningar", String(included.filter((g) => g.conflict).length)],
    ]),
    h1("Underlag som samordnats"),
    sourceTable(args.sources),
    h1("Samordnade risker"),
  ];

  const categories = Array.from(new Set(included.map((g) => g.category)));
  for (const cat of categories) {
    const rows = included.filter((g) => g.category === cat);
    children.push(h2(cat));
    children.push(
      table(
        ["Risk / fråga", "Källor", "Identifierad risk", "Åtgärd", "Risknivå", "Bas-P beslut"],
        rows.map((g) => {
          const r = args.resolutions[g.key];
          return [
            g.question,
            g.entries.map((e) => sourceLabel.get(e.sourceId) ?? "–").join(", "),
            g.entries.map((e) => e.risk).filter(Boolean).join("\n"),
            g.entries.map((e) => e.action).filter(Boolean).join("\n"),
            resolvedRiskLevel(g, r),
            [r?.decision, r?.motivation].filter(Boolean).join(" – ") ||
              (g.conflict ? "Beslut saknas" : "Samstämmigt"),
          ];
        }),
        { columnWidths: [2400, 1500, 2200, 2200, 900, 1400] }
      )
    );
  }

  if (args.notes) {
    children.push(h1("Kommentar från Bas-P"), body(args.notes));
  }

  const footer = await documentFooter({
    docNumber: "30c",
    documentType: "risksammanstallning_samordnad",
    signatureRoles: ["Bas-P", "Projektör"],
  });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "30c",
      documentName,
      projectName: args.projectName,
      version: args.version || "1.0",
      status: args.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok30c_Samordnad_risksammanstallning_${(args.projectName || "projekt").replace(/[^\w-]+/g, "_")}.docx`,
    {
      documentType: "risksammanstallning_samordnad",
      docNumber: "30c",
      documentName,
      snapshot: { sources: args.sources, groups: included, resolutions: args.resolutions },
      stamp,
    }
  );
};

export const generateConsolidatedWorkAreaWord = async (
  args: BaseArgs & { groups: WorkAreaGroup[] }
) => {
  const documentName = args.title || "Samlad arbetsområdesutredning";
  const stamp = await createStampForCurrentUser({
    docNumber: "10b",
    documentName,
    projectName: args.projectName,
  });

  const sourceLabel = new Map(args.sources.map((s) => [s.id, s.label]));
  const included = args.groups.filter((g) => isIncluded(args.resolutions, g.key));

  const children: (Paragraph | Table)[] = [
    ...tableOfContents(),
    h1("Om dokumentet"),
    body(
      "Dokumentet samlar arbetsområdesutredningarna för projektets alla områden i en gemensam redovisning, " +
        "så att skillnader mellan områden syns direkt och kan hanteras av Bas-P."
    ),
    factTable([
      ["Antal arbetsområden", String(args.sources.length)],
      ["Antal kontrollpunkter", String(included.length)],
      ["Punkter med olika bedömning", String(included.filter((g) => g.conflict).length)],
    ]),
    h1("Arbetsområden som ingår"),
    sourceTable(args.sources),
    h1("Samlade kontrollpunkter"),
  ];

  const categories = Array.from(new Set(included.map((g) => g.category)));
  for (const cat of categories) {
    const rows = included.filter((g) => g.category === cat);
    children.push(h2(cat));
    children.push(
      table(
        ["Kontrollpunkt", "Lagrum", "Bedömning per område", "Kommentar", "Bas-P beslut"],
        rows.map((g) => {
          const r = args.resolutions[g.key];
          return [
            g.controlPoint,
            g.legalReference || "",
            g.entries
              .map(
                (e) =>
                  `${sourceLabel.get(e.sourceId) ?? "–"}: ${
                    WORK_AREA_STATUS_LABELS[e.status ?? ""] ?? "–"
                  }`
              )
              .join("\n"),
            g.entries.map((e) => e.comment).filter(Boolean).join("\n"),
            [r?.decision, r?.motivation].filter(Boolean).join(" – ") ||
              (g.conflict ? "Beslut saknas" : "Samstämmigt"),
          ];
        }),
        { columnWidths: [2600, 1400, 2600, 2200, 1800] }
      )
    );
  }

  if (args.notes) {
    children.push(h1("Kommentar från Bas-P"), body(args.notes));
  }

  const footer = await documentFooter({
    docNumber: "10b",
    documentType: "arbetsomrade_samlad",
    signatureRoles: ["Bas-P", "Projektör"],
  });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "10b",
      documentName,
      projectName: args.projectName,
      version: args.version || "1.0",
      status: args.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok10b_Samlad_arbetsomradesutredning_${(args.projectName || "projekt").replace(/[^\w-]+/g, "_")}.docx`,
    {
      documentType: "arbetsomrade_samlad",
      docNumber: "10b",
      documentName,
      snapshot: { sources: args.sources, groups: included, resolutions: args.resolutions },
      stamp,
    }
  );
};

export { riskLevelLabel };
